import { Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import type { AuditLogEntry } from "../../../types/audit-log.types.ts"
import { getLeadLogHistory } from "../../Lead/api/services.ts"
import { getProjectLogHistory } from "../../Project/api/project.services.ts"
import { relativeTime } from "../helpers/relative-time.ts"
import { EntityGlyphTile } from "../map/EntityGlyphTile.tsx"
import type { EntityType } from "../redux/global-map.types"

const { Text } = Typography

// A preview, not the record's full history — the Logs/Activity screens on
// the record itself own that.
const PREVIEW_LIMIT = 8

export interface QuickViewActivityEntry {
  key: string
  action: string
  actor: string | null
  timestamp: string
}

/** The actor's display name, whichever of the backend's three shapes the row
 *  carries (`actor_name`, a nested actor object, or a bare id we cannot
 *  resolve and therefore do not show). */
function actorName(entry: AuditLogEntry): string | null {
  if (entry.actor_name) return entry.actor_name
  const actor = entry.actor
  if (actor && typeof actor === "object") {
    const parts = [
      (actor as { first_name?: string }).first_name,
      (actor as { last_name?: string }).last_name
    ].filter(Boolean)
    if (parts.length) return parts.join(" ")
    const name = actor as { full_name?: string; email?: string }
    return name.full_name ?? name.email ?? null
  }
  return null
}

export function toActivityEntries(
  rows: AuditLogEntry[]
): QuickViewActivityEntry[] {
  return [...rows]
    .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp))
    .slice(0, PREVIEW_LIMIT)
    .map((entry, index) => ({
      key: `${entry.id}-${index}`,
      action: [entry.action, entry.target].filter(Boolean).join(" · "),
      actor: actorName(entry),
      timestamp: entry.timestamp
    }))
}

interface QuickViewActivityProps {
  entityId: string
  entityType: EntityType
  onLoaded?: (count: number) => void
}

/**
 * The record's own recent history, read from the SAME log-history endpoint
 * the Lead workspace's Activity tab and the project's Logs screen already
 * use — no Global-Map-specific activity store, and nothing invented: a
 * record with no audit rows says so.
 */
export function QuickViewActivity({
  entityId,
  entityType,
  onLoaded
}: QuickViewActivityProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading")
  const [entries, setEntries] = useState<QuickViewActivityEntry[]>([])

  useEffect(() => {
    let cancelled = false
    setStatus("loading")

    const request =
      entityType === "lead"
        ? getLeadLogHistory(entityId)
        : getProjectLogHistory(entityId)

    void request.then((response) => {
      if (cancelled) return
      const rows = Array.isArray(response.data)
        ? (response.data as AuditLogEntry[])
        : []
      if (!response.ok) {
        setStatus("error")
        return
      }
      const mapped = toActivityEntries(rows)
      setEntries(mapped)
      setStatus("ready")
      onLoaded?.(rows.length)
    })

    return () => {
      cancelled = true
    }
    // `onLoaded` is intentionally not a dependency — it is a reporting
    // callback, and re-running this fetch because the parent re-rendered
    // would re-request on every keystroke elsewhere in the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType])

  if (status === "loading") {
    return <Skeleton active paragraph={{ rows: 4 }} title={false} />
  }

  if (status === "error") {
    return (
      <Text type="secondary" className="text-[13px]">
        {t("globalMap.quickView.activity.loadError")}
      </Text>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-slate-200 py-6">
        <Text className="text-[13px] text-slate-500">
          {t("globalMap.quickView.activity.empty")}
        </Text>
      </div>
    )
  }

  return (
    <ul
      className="m-0 flex list-none flex-col gap-2 p-0"
      data-testid="global-map-quickview-activity"
    >
      {entries.map((entry) => (
        <li key={entry.key} className="flex items-start gap-2.5">
          <EntityGlyphTile entityType={entityType} size={28} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] font-medium text-slate-800">
              {entry.action}
            </span>
            <span className="truncate text-[11px] text-slate-400">
              {[entry.actor, relativeTime(entry.timestamp)]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}

export default QuickViewActivity
